import { encodeValue } from './encoding';
import type { BuildInput, Selection } from './types';

/** The reserved literal marking a skipped taxonomy level. */
const ALL = 'all';

/** The segment that separates the taxonomy part from the filter part. */
const SEPARATOR = '-';

/** Case-sensitive ASCII (lexicographic) ascending comparison. */
const ascending = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/**
 * Build a path-like product-discovery URL from a {@link BuildInput}.
 *
 * The output is always canonical: the caller's ordering is ignored.
 *
 * Behaviour:
 *  - `basePath` is emitted verbatim.
 *  - Each selection in `filters` is classified by whether its `alias` is in
 *    `taxonomyAliases`:
 *      - **taxonomy** selections are emitted as path segments after the base
 *        path, in `taxonomyAliases` order. Multi-select values are joined with
 *        `;`, sorted alphabetically within the segment.
 *      - **filter** selections are emitted after the `-` separator as one
 *        `alias`/`value` pair per value (the alias is repeated for multi-value
 *        filters), sorted by alias then value.
 *  - Interior taxonomy levels with no selection are emitted as the reserved
 *    `all` placeholder; trailing levels with no selection are trimmed.
 *  - The `-` separator segment is emitted only if there is at least one filter
 *    selection.
 *  - Each value is encoded as the inverse of {@link parse}'s decode.
 *
 * `build({ basePath, taxonomyAliases, filters: parse(url, { basePath, taxonomyAliases }) })`
 * reproduces `url` (modulo encoding/canonicalisation normalisation and the
 * equivalence of a trailing `all` with its absence).
 */
export const build = (input: BuildInput): string => {
  const { basePath, filters, taxonomyAliases } = input;

  const taxonomyAliasSet = new Set(taxonomyAliases);

  // Partition selections: taxonomy levels keyed by alias, filters flattened to
  // individual alias/value pairs.
  const taxonomyByAlias = new Map<string, Selection>();
  const filterPairs: Array<{ alias: string; value: string }> = [];
  filters.forEach((selection) => {
    if (taxonomyAliasSet.has(selection.alias)) {
      taxonomyByAlias.set(selection.alias, selection);
    } else {
      selection.values.forEach((value) => filterPairs.push({ alias: selection.alias, value }));
    }
  });

  // Taxonomy part: emit up to the deepest present level, filling interior gaps
  // with `all` and trimming trailing gaps. Multi-select values are sorted.
  const taxonomySegments: string[] = [];
  let deepestPresent = -1;
  taxonomyAliases.forEach((alias, index) => {
    if (taxonomyByAlias.has(alias)) {
      deepestPresent = index;
    }
  });
  for (let index = 0; index <= deepestPresent; index += 1) {
    const selection = taxonomyByAlias.get(taxonomyAliases[index]);
    if (selection) {
      const segment = [...selection.values].sort(ascending).map(encodeValue).join(';');
      taxonomySegments.push(segment);
    } else {
      taxonomySegments.push(ALL);
    }
  }

  // Filter part: one `alias`/`value` pair per value, sorted by alias then value.
  filterPairs.sort((a, b) => ascending(a.alias, b.alias) || ascending(a.value, b.value));
  const filterSegments: string[] = [];
  filterPairs.forEach(({ alias, value }) => {
    filterSegments.push(alias, encodeValue(value));
  });

  const segments = [...taxonomySegments];
  if (filterSegments.length > 0) {
    segments.push(SEPARATOR, ...filterSegments);
  }

  return segments.length > 0 ? `${basePath}/${segments.join('/')}` : basePath;
};
